/* ============================================================
   BOX BUILDER — JavaScript Module
   Gift Boutique BCN
   State management, rendering, Shopify cart API
   ============================================================ */

(function () {
  'use strict';

  const MESSAGE_MAX_LENGTH = 500;

  /* ------ HELPERS ------ */
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];
  const esc = (s) => {
    const d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  };

  const formatMoney = (cents) => {
    const fmt = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '{{amount}}€';
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    const amountWithComma = amount.replace('.', ',');
    return fmt
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  };

  /* ------ TOAST SYSTEM ------ */
  const toast = {
    container: null,
    init() {
      this.container = document.createElement('div');
      this.container.className = 'bb-toast-container';
      this.container.setAttribute('aria-live', 'polite');
      document.body.appendChild(this.container);
    },
    show(msg, type = 'error', duration = 3500) {
      const el = document.createElement('div');
      el.className = `bb-toast ${type}`;
      el.textContent = msg;
      this.container.appendChild(el);
      setTimeout(() => {
        el.classList.add('exit');
        setTimeout(() => el.remove(), 300);
      }, duration);
    },
  };

  /* ------ STATE ------ */
  const state = {
    currentStep: 1,
    box: null,       // { id, title, img, price, limit }
    items: [],       // [{ id, title, img, price, qty }]
    card: null,      // { id, title, img, price } | null
    message: '',
  };

  // Restore from sessionStorage
  function loadState() {
    try {
      const saved = sessionStorage.getItem('bb_state');
      if (saved) {
        const parsed = JSON.parse(saved);
        Object.assign(state, parsed);
      }
    } catch (e) { /* ignore */ }
  }

  function saveState() {
    try {
      sessionStorage.setItem('bb_state', JSON.stringify(state));
    } catch (e) { /* ignore */ }
  }

  /* ------ RENDERING ------ */
  function updateStepper() {
    for (let i = 1; i <= 4; i++) {
      const step = $(`#bb-nav-${i}`);
      if (!step) continue;
      step.classList.remove('active', 'completed');
      if (i === state.currentStep) step.classList.add('active');
      else if (i < state.currentStep) step.classList.add('completed');
    }
    // Lines between steps
    $$('.bb-step-line').forEach((line, idx) => {
      line.classList.toggle('filled', idx + 1 < state.currentStep);
    });
  }

  function updateCapacity() {
    const counter = $('#bb-item-counter');
    const fill = $('#bb-capacity-fill');
    const badge = $('#bb-capacity-badge');
    if (!counter) return;
    const limit = state.box ? state.box.limit : 5;
    const count = state.items.reduce((s, i) => s + i.qty, 0);
    counter.textContent = `${count} / ${limit} ${STRINGS.txtSelected}`;
    if (fill) {
      fill.style.width = `${Math.min((count / limit) * 100, 100)}%`;
    }
    if (badge) {
      badge.classList.toggle('full', count >= limit);
    }
  }

  function updateItemCardStates() {
    $$('.bb-item-card').forEach((card) => {
      const id = parseInt(card.dataset.variantId, 10);
      const found = state.items.find((i) => i.id === id);
      card.classList.toggle('selected', !!found);
      const qtyBadge = card.querySelector('.bb-card-qty');
      if (qtyBadge) {
        qtyBadge.textContent = found ? (found.qty > 1 ? `×${found.qty}` : '✓') : '';
      }
    });
  }

  function updateSidebar() {
    const container = $('#bb-sidebar-items');
    const emptyState = $('#bb-sidebar-empty');
    const totalEl = $('#bb-sidebar-total');
    if (!container) return;

    let html = '';
    let total = 0;

    if (state.box) {
      html += sidebarItem(state.box.img, state.box.title, state.box.price, 'box');
      total += state.box.price;
    }

    state.items.forEach((item, idx) => {
      html += sidebarItem(item.img, item.title + (item.qty > 1 ? ` ×${item.qty}` : ''), item.price * item.qty, `item-${idx}`);
      total += item.price * item.qty;
    });

    if (state.card) {
      html += sidebarItem(state.card.img, state.card.title, state.card.price, 'card');
      total += state.card.price;
    }

    container.innerHTML = html;
    if (emptyState) emptyState.style.display = html ? 'none' : 'block';
    if (totalEl) totalEl.textContent = formatMoney(total);

    // Sidebar badge count
    const badge = $('#bb-sidebar-badge');
    const count = (state.box ? 1 : 0) + state.items.length + (state.card ? 1 : 0);
    if (badge) badge.textContent = count;

    // Footer total (mobile)
    const footerTotal = $('#bb-footer-total-amount');
    if (footerTotal) footerTotal.textContent = formatMoney(total);
  }

  function sidebarItem(img, title, price, key) {
    const imgHtml = img ? `<img src="${esc(img)}" class="bb-sidebar-item-img" alt="" loading="lazy">` : '';
    return `<div class="bb-sidebar-item" data-key="${esc(key)}">
      ${imgHtml}
      <div class="bb-sidebar-item-info">
        <span class="bb-sidebar-item-name">${esc(title)}</span>
        <span class="bb-sidebar-item-price">${formatMoney(price)}</span>
      </div>
    </div>`;
  }

  function renderReview() {
    const container = $('#bb-review-list');
    if (!container) return;
    let html = '';
    let total = 0;

    // Box
    if (state.box) {
      html += `<div class="bb-review-section-label">${esc(STRINGS.boxTitle)}</div>`;
      html += reviewRow(state.box.img, state.box.title, '', state.box.price);
      total += state.box.price;
    }

    // Items
    if (state.items.length) {
      html += `<div class="bb-review-section-label">${esc(STRINGS.itemTitle)} (${state.items.reduce((s, i) => s + i.qty, 0)})</div>`;
      state.items.forEach((item) => {
        const label = item.qty > 1 ? `${item.title} ×${item.qty}` : item.title;
        html += reviewRow(item.img, label, '', item.price * item.qty);
        total += item.price * item.qty;
      });
    }

    // Card
    if (state.card) {
      html += `<div class="bb-review-section-label">${esc(STRINGS.cardTitle)}</div>`;
      html += reviewRow(state.card.img, state.card.title, '', state.card.price);
      total += state.card.price;
    }

    // Message
    if (state.message) {
      html += `<div class="bb-review-msg">
        <div class="bb-review-msg-label">${esc(STRINGS.msgIncluded)}</div>
        <div class="bb-review-msg-text">"${esc(state.message)}"</div>
      </div>`;
    }

    container.innerHTML = html;
    const totalEl = $('#bb-final-total');
    if (totalEl) totalEl.textContent = formatMoney(total);
  }

  function reviewRow(img, title, sub, price) {
    const imgHtml = img ? `<img src="${esc(img)}" class="bb-review-img" alt="${esc(title)}" loading="lazy">` : '';
    return `<div class="bb-review-row">
      ${imgHtml}
      <div class="bb-review-info">
        <span class="bb-review-title">${esc(title)}</span>
        ${sub ? `<span class="bb-review-sub">${esc(sub)}</span>` : ''}
      </div>
      <div class="bb-review-price">${formatMoney(price)}</div>
    </div>`;
  }

  /* ------ ACTIONS ------ */
  function selectBox(id, title, img, price, limit, el) {
    state.box = { id, title, img, price, limit };
    state.items = [];
    saveState();

    $$('#bb-panel-1 .bb-card').forEach((c) => c.classList.remove('selected'));
    el.classList.add('selected');
    updateItemCardStates();
    updateCapacity();
    updateSidebar();

    setTimeout(() => goToStep(2), 350);
  }

  function toggleItem(id, title, img, price, el) {
    if (!state.box) {
      toast.show(STRINGS.errSelectBox);
      return;
    }
    const limit = state.box.limit;
    const idx = state.items.findIndex((i) => i.id === id);
    const totalQty = state.items.reduce((s, i) => s + i.qty, 0);

    if (idx > -1) {
      // Already selected — remove
      state.items.splice(idx, 1);
      el.classList.remove('selected');
    } else {
      if (totalQty >= limit) {
        toast.show(STRINGS.errBoxFull.replace('[LIMIT]', limit));
        return;
      }
      state.items.push({ id, title, img, price, qty: 1 });
      el.classList.add('selected');
    }

    saveState();
    updateCapacity();
    updateItemCardStates();
    updateSidebar();
  }

  function selectCard(id, title, img, price, el) {
    state.card = id === null ? null : { id, title, img, price };
    $$('#bb-panel-3 .bb-card').forEach((c) => c.classList.remove('selected'));
    el.classList.add('selected');
    saveState();
    updateSidebar();
  }

  function saveMessage(val) {
    state.message = val;
    saveState();
    // Update char count
    const counter = $('#bb-char-count');
    if (counter) counter.textContent = `${val.length} / ${MESSAGE_MAX_LENGTH}`;
  }

  /* ------ NAVIGATION ------ */
  function goToStep(step) {
    if (step > 1 && !state.box) {
      toast.show(STRINGS.errSelectBox);
      return;
    }
    state.currentStep = step;
    saveState();

    // Swap content panels
    $$('.bb-step-content').forEach((el) => el.setAttribute('hidden', ''));
    const target = $(`#bb-panel-${step}`);
    if (target) {
      target.removeAttribute('hidden');
      // Re-trigger animation
      target.style.animation = 'none';
      target.offsetHeight; // reflow
      target.style.animation = '';
    }

    updateStepper();

    // Prev button
    const prevBtn = $('#bb-prev');
    if (prevBtn) prevBtn.style.visibility = step === 1 ? 'hidden' : 'visible';

    // Next button
    const nextBtn = $('#bb-next');
    if (nextBtn) {
      if (step === 4) {
        nextBtn.textContent = STRINGS.btnAdd;
        nextBtn.onclick = addToCart;
        renderReview();
      } else {
        nextBtn.textContent = STRINGS.btnNext;
        nextBtn.onclick = () => goToStep(state.currentStep + 1);
      }
    }

    // Restore item card states when returning to step 2
    if (step === 2) {
      updateItemCardStates();
      updateCapacity();
    }

    // Restore card state when returning to step 3
    if (step === 3) {
      // Restore message in textarea
      const ta = $('#bb-message');
      const counter = $('#bb-char-count');
      if (ta) ta.value = state.message || '';
      if (counter) counter.textContent = `${state.message.length} / ${MESSAGE_MAX_LENGTH}`;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /* ------ CART API ------ */
  function addToCart() {
    const btn = $('#bb-next');
    if (!btn || !state.box) return;
    btn.textContent = STRINGS.btnAdding;
    btn.disabled = true;

    const uniqueId = Math.floor(1000 + Math.random() * 9000);
    const bundleRef = `#${uniqueId}`;
    const bundleHidden = `Ref_${Date.now()}`;

    const itemsToAdd = [];

    // Box
    const boxProps = {
      'Box Ref': bundleRef,
      '_Bundle': bundleHidden,
    };
    if (state.message) boxProps['Gift Message'] = state.message;
    itemsToAdd.push({ id: state.box.id, quantity: 1, properties: boxProps });

    // Items
    state.items.forEach((i) => {
      itemsToAdd.push({
        id: i.id,
        quantity: i.qty,
        properties: {
          'Item For': `${state.box.title} (${bundleRef})`,
          '_Bundle': bundleHidden,
        },
      });
    });

    // Card
    if (state.card) {
      itemsToAdd.push({
        id: state.card.id,
        quantity: 1,
        properties: {
          'Item For': `${state.box.title} (${bundleRef})`,
          '_Bundle': bundleHidden,
        },
      });
    }

    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: itemsToAdd }),
    })
      .then((res) => {
        if (res.ok) {
          toast.show(STRINGS.btnAdd + ' ✓', 'success', 2000);
          // Clear state
          sessionStorage.removeItem('bb_state');
          setTimeout(() => { window.location.href = '/cart'; }, 600);
        } else {
          throw new Error('Cart error');
        }
      })
      .catch(() => {
        toast.show(STRINGS.errCart);
        btn.textContent = STRINGS.btnAdd;
        btn.disabled = false;
      });
  }

  /* ------ INIT ------ */
  function init() {
    toast.init();
    loadState();

    // If returning with state, restore visual selections
    if (state.box) {
      // Re-select box card
      $$('#bb-panel-1 .bb-card').forEach((card) => {
        const varId = parseInt(card.dataset.variantId, 10);
        if (varId === state.box.id) card.classList.add('selected');
      });
    }

    // Always start at step 1 on fresh load for clarity
    state.currentStep = 1;
    goToStep(1);
    updateSidebar();
  }

  /* ------ PUBLIC API ------ */
  window.boxBuilder = {
    selectBox,
    toggleItem,
    selectCard,
    saveMessage,
    goToStep,
    nextStep: () => { if (state.currentStep < 4) goToStep(state.currentStep + 1); },
    prevStep: () => { if (state.currentStep > 1) goToStep(state.currentStep - 1); },
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
